import * as Sentry from '@sentry/react';
import React from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType
} from 'react-router-dom';

import { API_URL } from 'src/settings';
import { ImpersonatingInfo } from 'src/shared/types/user';
import { isCircularDomSerializationError } from 'src/shared/utils/domSerializationGuard';
import { isDynamicImportFetchError } from 'src/shared/utils/dynamicImportFetchErrorGuard';
import { isNonErrorPromiseRejection } from 'src/shared/utils/nonErrorPromiseRejectionGuard';

/**
 * Sentry `beforeSend` hook, extracted as a standalone export so it can be
 * unit tested directly against realistic `Event`/`EventHint` fixtures
 * without needing to boot the SDK or a browser (see `reporting.test.ts`).
 */
export function sentryBeforeSend(
  event: Sentry.ErrorEvent,
  hint: Sentry.EventHint
): Sentry.ErrorEvent | null {
  // Drop events caused by third-party browser extensions that patch
  // `appendChild` and call `JSON.stringify` on the inserted node, which
  // trips on React's fiber cycle. These are not our bugs and produce
  // significant noise. A genuine circular-JSON bug in our own code will
  // NOT match the predicate (it checks for `__reactFiber`/`__reactProps`).
  if (isCircularDomSerializationError(hint?.originalException)) {
    return null;
  }

  // Drop promises rejected with a non-Error value (typically a raw DOM
  // Event from a third-party script/browser extension re-dispatching an
  // `unhandledrejection`, or an empty object). These never carry a usable
  // stack trace or message regardless of origin, so they're never
  // actionable even if kept — dropping them outright avoids Sentry/Slack
  // noise without losing any real diagnostic capability.
  if (isNonErrorPromiseRejection(hint?.originalException)) {
    return null;
  }

  // Drop failed Vite/Chrome dynamic `import()`s of vendor chunks (see
  // `isDynamicImportFetchError`). Also match the serialized exception
  // value so a breadcrumb-only payload without `originalException` is
  // dropped the same way.
  if (
    isDynamicImportFetchError(hint?.originalException) ||
    event.exception?.values?.some((value) =>
      value.value?.includes('Failed to fetch dynamically imported module')
    )
  ) {
    return null;
  }

  return event;
}

export function init({
  environment,
  debugModeEnabled = false
}: {
  environment: string;
  debugModeEnabled: boolean;
}) {
  const enabled = !environment.startsWith('local');

  Sentry.init({
    dsn: 'https://9643191e8aec0f45e15a6e8bf9f652b5@o4506826568695808.ingest.us.sentry.io/4506829241057280',
    sendDefaultPii: true,
    debug: debugModeEnabled,
    enabled,
    environment,
    replaysSessionSampleRate: 1.0,
    replaysOnErrorSampleRate: 1.0,
    tracesSampleRate: 1.0,
    profilesSampleRate: debugModeEnabled ? 1.0 : 0.01,
    tracePropagationTargets: [
      API_URL || '',
      'https://api.cavela.com',
      'http://localhost:8000',
      'http://127.0.0.1:8000'
    ],
    beforeSend: sentryBeforeSend,
    integrations: [
      Sentry.replayCanvasIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
        maskAllInputs: false
      }),
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes
      })
    ]
  });
  // Default tag values
  Sentry.setTag('impersonating', false);
  Sentry.setTag('cavela', false);

  console.info(
    `[Reporting] Environment: ${environment}. Debug mode: ${debugModeEnabled}. Sentry enabled: ${enabled}`
  );
}

export function captureException({
  error,
  extra,
  level = 'error'
}: {
  error: Error;
  extra?: any;
  level?: Sentry.SeverityLevel;
}) {
  Sentry.withScope((scope) => {
    scope.setLevel(level);
    Sentry.captureException(error, { extra });
  });
}

export function setUser({
  id,
  email,
  username,
  impersonating
}: {
  id: string;
  email: string;
  username: string;
  impersonating: ImpersonatingInfo | null;
}) {
  const user = { id, email, username };

  if (impersonating) {
    const user_impersonation = {
      impersonatingId: impersonating.id,
      impersonatingUsername: impersonating.username
    };

    Sentry.setUser({ ...user, ...user_impersonation });
    Sentry.setTag('impersonating', true);
  } else {
    Sentry.setUser(user);
    Sentry.setTag('impersonating', false);
  }

  Sentry.setTag('cavela', email.includes('@cavela.com'));
}
